/* Custom text input box that only allows  *
 * integers or floats to be entered.       */
import React, { useCallback } from 'react';
import PropTypes from 'prop-types';

const Numbox = ({
    value,
    defaultValue,
    onChange,
    autoComplete = false,
    disabled = false,
    disabledString = '-',
    allowFloat = false,
    disallowNegatives = false,
    digitLimit,
    onFocus,
    inputStyle,
    style = inputStyle,
    bypassValidation = false,
    ...props
}) => {
    const reI = disallowNegatives ? /^\d+$/ : /^[+-]?\d+$/;
    const reF = disallowNegatives
        ? /^((\.\d+)|(\d+(\.\d+)?))$/
        : /^[+-]?((\.\d+)|(\d+(\.\d+)?))$/;
    const rePartialF = disallowNegatives
        ? /^(\.0*|\.0*|\d+\.)?$/
        : /^([+-]|\.0*|[+-]\.0*|[+-]?\d+\.)?$/;

    /**
     * Check if an input is a valid numerical value, based on the given parameters
     * @param {*} v - value
     */
    const isValid = (v) => {
        const s = `${v}`;
        // Allow the text box to be blank
        const retVal =
            s === undefined ||
            s === '' ||
            // If floats aren't allowed, validate as integer
            (!allowFloat &&
                reI.test(s) &&
                (!digitLimit || s.length <= digitLimit)) || // If floats are allowed, then two integers may be sperated by a `.`
            (allowFloat &&
                (reF.test(s) || rePartialF.test(s)) &&
                (!digitLimit ||
                    s.length <=
                        digitLimit +
                            (s.includes('.') +
                                (!disallowNegatives &&
                                    s.includes(disabledString)))));

        return retVal;
    };

    /**
     * onChange event handler for numbox text input. Will not fire onChange if target.value is invalid UNLESS bypassValidation is set (useful for special characters, outside the regex e.g. %)
     * @param {Event} e - event
     */
    const handleInput = (e) => {
        if (
            disabled ||
            typeof onChange !== 'function' ||
            (!bypassValidation && !isValid(e.target.value))
        ) {
            return;
        }
        onChange(e);
    };

    // Fix for chromium-based browsers forcing autocomplete
    const handleFocus = useCallback(
        (event) => {
            if (typeof onFocus === 'function') onFocus(event);
            if (autoComplete) return;
            event.target.autocomplete = 'off';
        },
        [autoComplete, onFocus]
    );

    const size = digitLimit ? digitLimit + allowFloat : undefined;

    return (
        <input
            {...props}
            value={disabled && !isValid(value) ? disabledString : value}
            defaultValue={defaultValue}
            disabled={disabled}
            onChange={handleInput}
            onFocus={handleFocus}
            maxLength={size}
            size={size}
            type='text'
            style={{
                boxSizing: 'border-box',
                padding: '0px 5px',
                border: '1px solid rgb(215,215,215)',
                borderRadius: 3,
                backgroundColor: disabled
                    ? 'rgb(247,247,247)'
                    : 'rgb(255,253,244)',
                fontSize: 12,
                fontWeight: 'bold',
                fontFamily: 'roboto',
                textAlign: 'right',
                ...style
            }}
        />
    );
};

Numbox.defaultProps = {
    autoComplete: false,
    disabled: false,
    disabledString: '-',
    allowFloat: false,
    disallowNegatives: false,
    bypassValidation: false
};

Numbox.propTypes = {
    value: PropTypes.any,
    defaultValue: PropTypes.any,
    onChange: PropTypes.func,
    autoComplete: PropTypes.bool,
    disabled: PropTypes.bool,
    disabledString: PropTypes.string,
    allowFloat: PropTypes.bool,
    disallowNegatives: PropTypes.bool,
    digitLimit: PropTypes.number,
    onFocus: PropTypes.func,
    style: PropTypes.object,
    inputStyle: PropTypes.object,
    bypassValidation: PropTypes.bool
};

export default Numbox;
